import axios from 'axios';
import { useAuthStore } from '../stores/auth';
import { useToast } from './useToast';

// Add /api prefix for production URLs
const baseURL = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

// Create a public instance without auth
const publicApi = axios.create({
    baseURL,
    timeout: 30000
});

// Create an authenticated instance
const authApi = axios.create({
    baseURL,
    timeout: 30000
});

// Add request interceptor to authenticated instance
authApi.interceptors.request.use(
    async (config) => {
        const authStore = useAuthStore();
        const token = authStore.token;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            //console.log('Adding auth token to request:', config.url);
        } else {
            console.warn('No auth token available for request:', config.url);
        }

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor to both instances
const addResponseInterceptor = (instance) => {
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const toast = useToast();
            const authStore = useAuthStore();

            // Handle network errors
            if (!error.response) {
                toast.error('Network error. Please check your connection.');
                return Promise.reject(error);
            }

            // Check if this is an expected permission error (e.g., /users or /employees endpoint for staff)
            // These are handled gracefully by the calling code, so we don't need to show toasts
            const isExpectedPermissionError = error.response.status === 403 && (
                (error.config.url?.includes('/users') && error.response.data?.missingPermissions?.includes('users.view')) ||
                (error.config.url?.includes('/employees') && error.response.data?.missingPermissions?.some(p => p.includes('employees')))
            );

            // Log the error response for debugging (use console.log for expected errors)
            if (isExpectedPermissionError) {
                console.log('API Permission Error (expected, handled gracefully):', {
                    status: error.response.status,
                    url: error.config.url,
                    method: error.config.method
                });
            } else {
                console.error('API Error:', {
                    status: error.response.status,
                    url: error.config.url,
                    method: error.config.method,
                    data: error.response.data
                });
            }

            // Handle specific error codes
            switch (error.response.status) {
                case 401:
                    if (authStore.isAuthenticated) {
                        await authStore.logout();
                        toast.error('Session expired. Please log in again.');
                    }
                    break;
                case 403:
                    // Skip toast for expected permission errors (handled by calling code)
                    if (isExpectedPermissionError) {
                        // Just log and continue - the calling code will handle the fallback
                        break;
                    }
                    
                    // Check if we have specific permission information
                    const errorData = error.response.data;
                    let permissionMessage = 'Access denied. You do not have permission to perform this action.';
                    
                    if (errorData.requiredPermission) {
                        // Single permission missing
                        permissionMessage = `Access denied. Missing permission: ${errorData.requiredPermission}`;
                    } else if (errorData.missingPermissions && errorData.missingPermissions.length > 0) {
                        // Multiple permissions missing
                        if (errorData.requiresAny) {
                            permissionMessage = `Access denied. You need at least one of these permissions: ${errorData.missingPermissions.join(', ')}`;
                        } else if (errorData.requireAll) {
                            permissionMessage = `Access denied. Missing required permissions: ${errorData.missingPermissions.join(', ')}`;
                        } else {
                            permissionMessage = `Access denied. Missing permissions: ${errorData.missingPermissions.join(', ')}`;
                        }
                    } else if (errorData.requiredPermissions && errorData.requiredPermissions.length > 0) {
                        // Fallback to showing required permissions
                        permissionMessage = `Access denied. Required permissions: ${errorData.requiredPermissions.join(', ')}`;
                    }
                    
                    toast.error(permissionMessage);
                    if (!isExpectedPermissionError) {
                        console.error('Permission denied:', error.response.data);
                    }
                    break;
                case 404:
                    console.error('API 404 Error:', {
                        url: error.config.url,
                        method: error.config.method,
                        baseURL: error.config.baseURL
                    });
                    toast.error(`API endpoint not found: ${error.config.method.toUpperCase()} ${error.config.url}`);
                    break;
                case 429:
                    toast.error('Too many requests. Please try again later.');
                    break;
                case 500:
                    toast.error('Server error. Please try again later.');
                    break;
                default:
                    if (error.response.data?.error) {
                        toast.error(error.response.data.error);
                    }
            }

            return Promise.reject(error);
        }
    );
};

// Apply response interceptor to both instances
addResponseInterceptor(publicApi);
addResponseInterceptor(authApi);

export function useApi(isPublic = false) {
    return isPublic ? publicApi : authApi;
} 